import jsPDF from "jspdf";
import "jspdf-autotable";
import { EXPORT_FILE_NAME, TABLE_HEADERS, STATUS } from "./constants";

/**
 * Utility functions for product availability operations
 * Provides pure functions for data transformation and export
 */

/**
 * Get current date time formatted as DD/MM/YYYY
 * @returns {string} Formatted date string
 */
export const getCurrentDateTime = () => {
  const now = new Date();
  return `${String(now.getDate()).padStart(2, "0")}/${String(
    now.getMonth() + 1,
  ).padStart(2, "0")}/${now.getFullYear()}`;
};

/**
 * Flatten products with variants into a single array for display
 * Handles both products with variants and without variants
 *
 * @param {Array} products - Products array
 * @returns {Array} Flattened product rows
 */
export const flattenProducts = (products) => {
  return products.flatMap((product) => {
    if (product.variants && product.variants.length > 0) {
      return product.variants.map((variant) => ({
        id: `${product._id}-${variant._id}`,
        productNumber: product.productNumber || "",
        productName: `${product.productName || ""}`,
        categoryName: product.categoryName || "",
        subcategoryName: product.subcategoryName || "",
        price: variant.price ?? "-",
        offerPrice: variant.offerPrice ?? "-",
        status:
          variant.isVisible || product.isVisible
            ? STATUS.AVAILABLE
            : STATUS.NOT_AVAILABLE,
      }));
    } else {
      return [
        {
          id: product._id,
          productNumber: product.productNumber || "",
          productName: product.productName || "",
          categoryName: product.categoryName || "",
          subcategoryName: product.subcategoryName || "",
          price: product.price ?? "-",
          offerPrice: product.offerPrice ?? "-",
          status: product.isVisible ? STATUS.AVAILABLE : STATUS.NOT_AVAILABLE,
        },
      ];
    }
  });
};

/**
 * Filter products based on search query
 * Searches across product name, number, category, and subcategory
 *
 * @param {Array} products - Products array
 * @param {string} searchQuery - Search query string
 * @returns {Array} Filtered products
 */
export const filterProducts = (products, searchQuery) => {
  if (!searchQuery) return products;

  const query = searchQuery.toLowerCase();
  return products.filter(
    (product) =>
      product.productName.toLowerCase().includes(query) ||
      product.productNumber.toLowerCase().includes(query) ||
      product.categoryName.toLowerCase().includes(query) ||
      product.subcategoryName.toLowerCase().includes(query),
  );
};

/**
 * Export product availability to PDF
 *
 * @param {Array} products - Flattened products array
 */
export const exportAvailability = (products) => {
  const doc = new jsPDF();
  doc.text(`Product Availability - ${getCurrentDateTime()}`, 10, 10);

  const tableData = products.map((item) => [
    item.productNumber,
    item.productName,
    item.categoryName,
    item.subcategoryName,
    item.price,
    item.offerPrice,
    item.status,
  ]);

  doc.autoTable({
    startY: 20,
    head: [
      [
        TABLE_HEADERS.PRODUCT_NUMBER,
        TABLE_HEADERS.PRODUCT_NAME,
        TABLE_HEADERS.CATEGORY,
        TABLE_HEADERS.SUBCATEGORY,
        TABLE_HEADERS.PRICE,
        TABLE_HEADERS.OFFER_PRICE,
        TABLE_HEADERS.STATUS,
      ],
    ],
    body: tableData,
  });

  doc.save(EXPORT_FILE_NAME);
};

/**
 * Export product availability to CSV
 *
 * @param {Array} products - Flattened products array
 */
export const exportAvailabilityAsCSV = (products) => {
  // Create CSV header
  const headers = [
    TABLE_HEADERS.PRODUCT_NUMBER,
    TABLE_HEADERS.PRODUCT_NAME,
    TABLE_HEADERS.CATEGORY,
    TABLE_HEADERS.SUBCATEGORY,
    TABLE_HEADERS.PRICE,
    TABLE_HEADERS.OFFER_PRICE,
    TABLE_HEADERS.STATUS,
  ];

  // Create CSV rows
  const rows = products.map((item) => [
    item.productNumber,
    item.productName,
    item.categoryName,
    item.subcategoryName,
    item.price,
    item.offerPrice,
    item.status,
  ]);

  // Combine headers and rows
  const csvContent = [
    headers.join(","),
    ...rows.map((row) =>
      row
        .map((cell) => {
          // Escape quotes and wrap cells with commas in quotes
          const escapedCell = String(cell).replace(/"/g, '""');
          return `"${escapedCell}"`;
        })
        .join(","),
    ),
  ].join("\n");

  // Create blob and download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `Product_Availability_${getCurrentDateTime().replace(/\//g, "-")}.csv`,
  );
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Paginate data for display
 *
 * @param {Array} data - Data array
 * @param {number} page - Current page (1-indexed)
 * @param {number} rowsPerPage - Rows per page
 * @returns {Array} Paginated data
 */
export const paginateData = (data, page, rowsPerPage) => {
  return data.slice((page - 1) * rowsPerPage, page * rowsPerPage);
};
